/**
 * paymentSimulationEngine.js — Re Leve IA (Etapa 9)
 * Motor local de simulação de pagamentos para o front-end.
 * Usa localStorage. Nunca salva dados sensíveis (cartão, CVV, etc.).
 * Integra com a jornada comercial via eventos.
 */

const STORAGE_KEY = 'releve_payments';

// ── Preços simulados por kit ───────────────────────────────────────────────────
const KIT_PRICES = {
  'Kit Dia Leve':             59.90,
  'Kit Dia Controle':         69.90,
  'Kit Dia Performance':      79.90,
  'Semana Econômica':        249.90,
  'Semana Útil Resolvida':   499.90,
  'Semana Completa Re Leve': 699.90,
  'Família Organizada':     1490.00,
  'Escritório Saudável':    1990.00,
};

const DEFAULT_PRICE = 59.90;

export function getKitSimulatedPrice(kit) {
  if (!kit) return DEFAULT_PRICE;
  const name = typeof kit === 'string' ? kit : (kit.nome || kit.name || '');
  if (KIT_PRICES[name]) return KIT_PRICES[name];
  const key = Object.keys(KIT_PRICES).find(k => name.toLowerCase().includes(k.toLowerCase()));
  return key ? KIT_PRICES[key] : DEFAULT_PRICE;
}

function formatPrice(value) {
  return `R$ ${value.toFixed(2).replace('.', ',')}`;
}

// ── Acesso ao store ───────────────────────────────────────────────────────────

function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {}
}

function generatePaymentId() {
  const ts = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 6);
  return `lclpay_${ts}_${rnd}`;
}

// ── Criar pagamento local ─────────────────────────────────────────────────────

export function createLocalPayment({ clienteId, kit }) {
  const kitName = typeof kit === 'string' ? kit : (kit?.nome || kit?.name || 'Kit não informado');
  const price = getKitSimulatedPrice(kitName);
  const paymentId = generatePaymentId();

  const payment = {
    id: paymentId,
    clienteId: clienteId || 'demo',
    kitName,
    price,
    priceFormatted: formatPrice(price),
    currency: 'BRL',
    status: 'simulated_pending',
    provider: 'local_simulation',
    checkoutUrl: '#pagamento-simulado',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    events: [{
      type: 'payment_created',
      at: new Date().toISOString(),
    }],
    source: 'local',
  };

  const store = loadStore();
  store[paymentId] = payment;
  saveStore(store);

  return payment;
}

// ── Simular pagamento aprovado ────────────────────────────────────────────────

export function simulateLocalPaymentSuccess(paymentId) {
  const store = loadStore();
  const payment = store[paymentId];
  if (!payment) return null;

  payment.status = 'simulated_paid';
  payment.updatedAt = new Date().toISOString();
  payment.events.push({
    type: 'payment_succeeded',
    at: new Date().toISOString(),
    source: 'prototype_button',
  });

  store[paymentId] = payment;
  saveStore(store);
  return payment;
}

// ── Simular pagamento cancelado ───────────────────────────────────────────────

export function simulateLocalPaymentCancelled(paymentId) {
  const store = loadStore();
  const payment = store[paymentId];
  if (!payment) return null;

  payment.status = 'simulated_cancelled';
  payment.updatedAt = new Date().toISOString();
  payment.events.push({
    type: 'payment_cancelled',
    at: new Date().toISOString(),
  });

  store[paymentId] = payment;
  saveStore(store);
  return payment;
}

// ── Consultar status ──────────────────────────────────────────────────────────

export function getLocalPaymentStatus(paymentId) {
  const store = loadStore();
  return store[paymentId] || null;
}

// ── Listar pagamentos de um cliente ──────────────────────────────────────────

export function listLocalPayments(clienteId) {
  const store = loadStore();
  return Object.values(store).filter(p => p.clienteId === clienteId);
}

// ── Limpar pagamentos de um cliente ──────────────────────────────────────────

export function clearLocalPayments(clienteId) {
  const store = loadStore();
  const updated = {};
  for (const [id, payment] of Object.entries(store)) {
    if (payment.clienteId !== clienteId) updated[id] = payment;
  }
  saveStore(updated);
}

// ── Labels de status ──────────────────────────────────────────────────────────

export function getPaymentStatusLabel(status) {
  const labels = {
    simulated_pending:   'Aguardando pagamento (simulado)',
    simulated_paid:      'Pagamento aprovado (simulado)',
    simulated_cancelled: 'Pagamento cancelado (simulado)',
    checkout_created:    'Checkout criado (Stripe)',
    paid:                'Pago (Stripe)',
    failed:              'Falhou (Stripe)',
    disabled:            'Pagamentos desativados',
  };
  return labels[status] || status;
}

export function isPaymentApproved(status) {
  return status === 'simulated_paid' || status === 'paid';
}
